<template>
  <header class="gallery-header" data-test-id="gallery-header">
    <div class="gallery-header__images">
      <a
        :href="imageUrl"
        :target="linkTarget"
        class="gallery-header__main-image-link"
        data-test-id="gallery-main-thumbnail-link"
        @click="$emit('image-click')"
      >
        <EThumbnail
          data-test-id="gallery-thumbnail"
          class="gallery-header__thumbnail"
          :src="mainImage.thumbnailUrl"
          :alt="mainImage.title"
        />
      </a>
      <div class="gallery-header__grid">
        <a
          v-for="img in images"
          :key="img.id"
          class="gallery-header__image"
          data-test-id="gallery-image-link"
          :href="getImageUrl(img)"
          @click="$emit('image-click')"
        >
          <EThumbnail
            data-test-id="gallery-thumbnail"
            class="gallery-header__thumbnail"
            :src="img.thumbnailUrl"
            :alt="img.title"
          />
        </a>
        <EButton
          as="a"
          size="s"
          :href="moreUrl"
          variant="solid-white"
          class="gallery-header__more"
          data-test-id="gallery-header-more"
        >
          {{ $t('web.entities.moreimages') }}
        </EButton>
      </div>
    </div>
    <ETitles
      :title="title"
      :subtitle="subtitle"
      class="gallery-header__title"
      data-test-id="entity-titles"
    />
  </header>
</template>
<script>
import { mapGetters } from 'vuex';

import { getUrlQuery } from '~~/common/js/universal/search';
import { getImageUrlHash } from '~~/search/common/js/universal/hash-params';
import EButton from '~~/common/vue/components/button';
import EThumbnail from '~~/common/vue/components/thumbnail';
import ETitles from './titles';

const IMAGES_PATH = '/images';

export default {
  name: 'EGalleryHeader',
  components: {
    EButton,
    EThumbnail,
    ETitles,
  },
  props: {
    title: ETitles.props.title,
    subtitle: ETitles.props.subtitle,
    imageUrl: {
      type: String,
      default: null,
    },
    image: {
      type: Object,
      default: null,
    },
    gallery: {
      type: Array,
      required: true,
    },
    moreImagesUrl: {
      type: String,
      default: '',
    },
    preferImageSourceUrl: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters('user', ['linkTarget']),
    mainImage() {
      // Use first gallery image as main
      return this.image?.thumbnailUrl ? this.image : this.gallery[0];
    },
    hasMainImage() {
      return !!this.image?.thumbnailUrl;
    },
    images() {
      // Skip first gallery image
      const images = this.hasMainImage ? this.gallery : this.gallery.slice(1);
      return images.slice(0, 4);
    },
    moreUrl() {
      if (this.moreImagesUrl) {
        return this.moreImagesUrl;
      }

      const query = getUrlQuery(this.$route.query, this.$store.state.user);
      return this.$router.resolve({ path: '/images', query }).href;
    },
  },
  methods: {
    getImageUrl({ id, sourceUrl }) {
      if (this.preferImageSourceUrl && sourceUrl) {
        return sourceUrl;
      }

      const hash = getImageUrlHash(id);
      const query = { q: this.$route.query.q?.toString() };
      return this.$router.resolve({ path: IMAGES_PATH, hash, query }).href;
    },
  },
};
</script>

<style lang="scss" scoped>
.gallery-header__images {
  display: flex;
}

.gallery-header__main-image-link {
  flex: 1;
  height: $entity-image-with-gallery-size;
}

.gallery-header__grid {
  position: relative;
  flex: 2;
  display: flex;
  flex-wrap: wrap;
  overflow: hidden;

  & > .gallery-header__image {
    flex: 50%;
    height: $entity-image-with-gallery-size * 0.5;
  }
}

.gallery-header__more {
  position: absolute;
  bottom: $space-1s;
  right: $space-1s;
}

@include tablet {
  .web__mainline {
    .gallery-header__image {
      flex: 1;
      height: $entity-image-with-gallery-size;
    }

    .gallery-header__main-image {
      flex: 1;
    }

    .gallery-header__grid {
      flex: 4;
    }
  }
}

.gallery-header__thumbnail {
  height: 100%;
}

.gallery-header__title {
  padding: $space-m $space-m 0;
}
</style>
